
import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Search, X, Loader2, Clock, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface EnhancedSearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
  initialValue?: string;
  autoFocus?: boolean;
  isSearching?: boolean;
  disabled?: boolean;
  suggestions?: string[];
  searchHistory?: string[];
  isLoadingSuggestions?: boolean;
  showSuggestions?: boolean;
  resultsCount?: number;
  onSuggestionSelect?: (suggestion: string) => void;
}

const EnhancedSearchBar = ({ 
  onSearch, 
  placeholder = "Search prompts...",
  className = "",
  initialValue = "",
  autoFocus = false,
  isSearching = false,
  disabled = false,
  suggestions = [],
  searchHistory = [],
  isLoadingSuggestions = false,
  showSuggestions = true,
  resultsCount,
  onSuggestionSelect
}: EnhancedSearchBarProps) => {
  const [query, setQuery] = useState(initialValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<number | null>(null);

  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  // Combine suggestions and history for dropdown
  const dropdownItems = [
    ...suggestions.map(s => ({ type: 'suggestion' as const, text: s })),
    ...(searchHistory.length > 0 && query.length === 0 ? 
      searchHistory.slice(0, 5).map(h => ({ type: 'history' as const, text: h })) : [])
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedIndex(-1);
    
    // Show dropdown when typing
    if (showSuggestions) {
      setShowDropdown(newQuery.length > 0 || searchHistory.length > 0);
    }
    
    // Clear existing timeout
    if (searchTimeout.current !== null) {
      window.clearTimeout(searchTimeout.current);
    }
    
    // Set new timeout for search
    searchTimeout.current = window.setTimeout(() => {
      onSearch(newQuery);
    }, 300);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || dropdownItems.length === 0) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < dropdownItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : dropdownItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < dropdownItems.length) {
          handleItemSelect(dropdownItems[selectedIndex].text);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleItemSelect = (item: string) => {
    setQuery(item);
    setShowDropdown(false);
    setSelectedIndex(-1);
    onSearch(item);
    onSuggestionSelect?.(item);
    inputRef.current?.focus();
  };

  const handleSearch = () => {
    onSearch(query);
    setShowDropdown(false);
  };

  const clearSearch = () => {
    setQuery("");
    setShowDropdown(false);
    setSelectedIndex(-1);
    onSearch("");
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFocus = () => {
    if (showSuggestions && (query.length > 0 || searchHistory.length > 0)) {
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    // Delay hiding dropdown to allow clicking on items
    setTimeout(() => setShowDropdown(false), 150);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("relative flex w-full items-center", className)} ref={dropdownRef}>
      <div className="relative w-full">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </div>
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="w-full pl-10 pr-10"
          autoFocus={autoFocus}
          disabled={disabled}
        />
        {query && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0" 
              onClick={clearSearch}
              disabled={disabled}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        )}
      </div>

      {/* Results count */}
      {resultsCount !== undefined && query && (
        <div className="absolute -bottom-6 left-0 text-xs text-muted-foreground">
          {resultsCount} result{resultsCount !== 1 ? 's' : ''}
        </div>
      )}

      {/* Suggestions dropdown */}
      {showDropdown && showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-64 overflow-hidden">
          <ScrollArea className="max-h-64">
            {isLoadingSuggestions ? (
              <div className="p-3 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                <span className="text-xs">Loading suggestions...</span>
              </div>
            ) : dropdownItems.length > 0 ? (
              <div className="py-1">
                {dropdownItems.map((item, index) => (
                  <button
                    key={`${item.type}-${item.text}`}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2",
                      selectedIndex === index && "bg-muted"
                    )}
                    onClick={() => handleItemSelect(item.text)}
                  >
                    {item.type === 'history' ? (
                      <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <TrendingUp className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="truncate">{item.text}</span>
                    {item.type === 'history' && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        Recent
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            ) : query.length > 0 ? (
              <div className="p-3 text-center text-muted-foreground text-sm">
                No suggestions found
              </div>
            ) : null}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default EnhancedSearchBar;
